import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material';
import { STATUS } from 'app/views/settings/constants';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { StatusBlock } from '.';

const DeleteQueueHistoryDialog = ({ open, onClose, onDelete }) => {
  const [deleteSettings, setDeletingSettings] = useState({
    deletedStatus: [STATUS.QUEUED, STATUS.RETRY, STATUS.SCHEDULED],
    deleteHistory: true
  });
  const [deletingQueued, setDeletingQueued] = useState(false);
  const deleteAllQueued = async () => {
    setDeletingQueued(true);
    try {
      const fn = httpsCallable(getFunctions(), 'wordpressRequest');
      const obj = {
        command: 'deleteAllQueued',
        settings: deleteSettings
      };
      const res = await fn(obj);
      toast.success('Queued items deleted successfully');
      window.location.reload();
    } catch (e) {
      console.log(e);
      toast.error('Error deleting queued items');
    }
    setDeletingQueued(false);
  };

  const removeStatus = (status) => {
    setDeletingSettings((prev) => {
      const newStatus = prev.deletedStatus.filter((s) => s !== status);
      return { ...prev, deletedStatus: newStatus };
    });
  };

  const addStatus = (status) => {
    setDeletingSettings((prev) => {
      const newStatus = [...prev.deletedStatus, status];
      return { ...prev, deletedStatus: newStatus };
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Clear Queue</DialogTitle>
      <DialogContent>
        {deleteSettings.deletedStatus.length === 0 ? (
          <Typography variant="body1" gutterBottom>
            Select one or more statuses to delete from the processing queue.
          </Typography>
        ) : (
          <Typography variant="body1" gutterBottom>
            This will remove all the items with status{' '}
            {deleteSettings.deletedStatus.map((status, index) => (
              <span key={status}>
                <b>{status}</b>
                {index < deleteSettings.deletedStatus.length - 1 ? ', ' : ' '}
              </span>
            ))}
            from the processing queue. Are you sure you want to proceed?
          </Typography>
        )}

        {/* make checkboxes */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Stack direction="column" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox checked={deleteSettings.deletedStatus.includes(STATUS.QUEUED)} />
                }
                label={<StatusBlock status={STATUS.QUEUED} />}
                onChange={(e) => {
                  if (e.target.checked) {
                    addStatus(STATUS.QUEUED);
                  } else {
                    removeStatus(STATUS.QUEUED);
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox checked={deleteSettings.deletedStatus.includes(STATUS.PROCESSING)} />
                }
                label={<StatusBlock status={STATUS.PROCESSING} />}
                onChange={(e) => {
                  if (e.target.checked) {
                    addStatus(STATUS.PROCESSING);
                  } else {
                    removeStatus(STATUS.PROCESSING);
                  }
                }}
              />

              <FormControlLabel
                control={<Checkbox checked={deleteSettings.deletedStatus.includes(STATUS.ERROR)} />}
                label={<StatusBlock status={STATUS.ERROR} />}
                onChange={(e) => {
                  if (e.target.checked) {
                    addStatus(STATUS.ERROR);
                  } else {
                    removeStatus(STATUS.ERROR);
                  }
                }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="column" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox checked={deleteSettings.deletedStatus.includes(STATUS.SCHEDULED)} />
                }
                label={<StatusBlock status={STATUS.SCHEDULED} />}
                onChange={(e) => {
                  if (e.target.checked) {
                    addStatus(STATUS.SCHEDULED);
                  } else {
                    removeStatus(STATUS.SCHEDULED);
                  }
                }}
              />

              <FormControlLabel
                control={<Checkbox checked={deleteSettings.deletedStatus.includes(STATUS.RETRY)} />}
                label={<StatusBlock status={STATUS.RETRY} />}
                onChange={(e) => {
                  if (e.target.checked) {
                    addStatus(STATUS.RETRY);
                  } else {
                    removeStatus(STATUS.RETRY);
                  }
                }}
              />
            </Stack>
          </Grid>
        </Grid>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Do you also want to delete the records of these items from the history?
        </Typography>
        <RadioGroup
          value={deleteSettings.deleteHistory}
          onChange={(e) =>
            setDeletingSettings({ ...deleteSettings, deleteHistory: e.target.value })
          }
        >
          <FormControlLabel value={true} control={<Radio />} label="Yes, Delete History" />
          <FormControlLabel value={false} control={<Radio />} label="No, Keep History" />
        </RadioGroup>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} color="primary">
          Cancel
        </Button>

        <LoadingButton
          disabled={deleteSettings.deletedStatus.length === 0}
          variant="contained"
          onClick={deleteAllQueued}
          color="error"
          loading={deletingQueued}
        >
          continue
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteQueueHistoryDialog;
